import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from "antd";
import { PlusOutlined, UploadOutlined } from "@ant-design/icons";
import moment from "moment";
import _ from "lodash";

// Firebase
import firebase from "firebase/compat/app";

const { Paragraph, Text } = Typography;

// Certificats clients d'un compte de service (LRE via l'API, décision 02/10/2026) : pour déposer
// une lettre recommandée électronique, l'API exige, en plus de la clé, un certificat enregistré
// ici et présenté par le serveur du client à chaque connexion (TLS mutuel). Seul le certificat
// public est collé : la clé privée reste chez le client. Gestion par les administrateurs de la
// société ; lecture seule pour le staff Solidoc (back-office, connexion déléguée).

const OPENSSL_COMMAND =
  'openssl req -x509 -newkey rsa:3072 -sha256 -days 730 -nodes -keyout client.key -out client.crt -subj "/CN=Votre société - Service"';

function formatDate(timestamp) {
  return timestamp ? moment(timestamp.toDate()).format("DD/MM/YYYY") : "—";
}

function formatDateTime(timestamp) {
  return timestamp ? moment(timestamp.toDate()).format("DD/MM/YYYY HH:mm") : "Jamais";
}

function certificateStatus(certificate) {
  const now = new Date();
  if (certificate.revoked_at) return { label: "Révoqué", color: "default" };
  if (certificate.not_after && certificate.not_after.toDate() <= now) {
    return { label: "Expiré", color: "orange" };
  }
  if (certificate.not_before && certificate.not_before.toDate() > now) {
    return { label: "Pas encore valide", color: "blue" };
  }
  return { label: "Actif", color: "green" };
}

function CertificateModal({ visible, account, onSubmit, onCancel, loading }) {
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible) form.resetFields();
  }, [visible, account, form]);

  // Fichier lu dans le navigateur : son contenu remplit la zone de texte, rien n'est téléversé
  function readFile(file) {
    const reader = new FileReader();
    reader.onload = () => form.setFieldsValue({ pem: String(reader.result || "") });
    reader.onerror = () => message.error("Lecture du fichier impossible");
    reader.readAsText(file);
    return false;
  }

  return (
    <Modal
      visible={visible}
      title={`Nouveau certificat — ${account?.name || ""}`}
      okText="Enregistrer"
      cancelText="Annuler"
      width={680}
      maskClosable={false}
      confirmLoading={loading}
      onCancel={onCancel}
      onOk={() =>
        form
          .validateFields()
          .then((values) => onSubmit(values))
          .catch(() => {})
      }
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="Collez uniquement le certificat public : ne transmettez jamais la clé privée."
        description={
          <>
            Pour générer une paire valable deux ans, sur le serveur qui appellera l'API :
            <Paragraph
              code
              copyable={{ text: OPENSSL_COMMAND, tooltips: ["Copier", "Copié"] }}
              style={{ margin: "8px 0" }}
            >
              {OPENSSL_COMMAND}
            </Paragraph>
            La clé privée (client.key) reste sur ce serveur ; collez ici le contenu de client.crt.
            Un certificat émis par l'autorité de votre société convient aussi.
          </>
        }
      />
      <Form layout="vertical" form={form} initialValues={{ label: "", pem: "" }}>
        <Form.Item
          name="label"
          label="Libellé"
          extra="Pour reconnaître le certificat (ex. : Serveur de paie)."
          rules={[
            { required: true, whitespace: true, message: "Veuillez entrer un libellé" },
            { max: 80, message: "80 caractères au plus" },
          ]}
        >
          <Input placeholder="Ex : Serveur de paie" />
        </Form.Item>
        <Form.Item
          name="pem"
          label="Certificat (format PEM)"
          rules={[{ required: true, whitespace: true, message: "Veuillez coller le certificat" }]}
        >
          <Input.TextArea
            rows={8}
            style={{ fontFamily: "monospace" }}
            placeholder={"-----BEGIN CERTIFICATE-----\n…\n-----END CERTIFICATE-----"}
          />
        </Form.Item>
        <Upload accept=".crt,.pem,.cer" showUploadList={false} beforeUpload={readFile}>
          <Button icon={<UploadOutlined />}>Importer un fichier</Button>
        </Upload>
      </Form>
    </Modal>
  );
}

export default function Certificates({
  account,
  companyId,
  readOnly = false,
  runCallable,
  saving = false,
  lastUpdate,
}) {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const accountId = account.id;

  useEffect(() => {
    if (!accountId || !companyId) return undefined;
    let cancelled = false;
    setLoading(true);

    const db = firebase.firestore();
    // Le filtre sur la société est exigé par les règles pour une requête de liste
    db.collection("service_accounts")
      .doc(accountId)
      .collection("certificates")
      .where("company", "==", db.collection("companies").doc(companyId))
      .get()
      .then((snap) => {
        if (cancelled) return;
        setCertificates(
          _.orderBy(
            snap.docs.map((doc) => ({ id: doc.id, key: doc.id, ...doc.data() })),
            (certificate) => (certificate.created_at ? certificate.created_at.toMillis() : 0),
            "desc"
          )
        );
      })
      .catch((e) => {
        console.error("Error loading certificates:", e);
        message.error("Erreur lors du chargement des certificats");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [accountId, companyId, lastUpdate]);

  // runCallable (écran Accès API) relance le chargement via `lastUpdate`
  async function handleAdd(values) {
    const result = await runCallable(
      "addServiceAccountCertificate",
      { serviceAccountId: accountId, label: values.label.trim(), pem: values.pem },
      "Certificat enregistré"
    );
    if (result) setModalVisible(false);
  }

  const columns = [
    { title: "Libellé", dataIndex: "label", key: "label" },
    {
      title: "Sujet",
      dataIndex: "subject",
      key: "subject",
      render: (value) => <Text type="secondary">{value}</Text>,
    },
    {
      title: "Empreinte SHA-256",
      key: "fingerprint",
      render: (_value, record) => (
        <Text code copyable={{ text: record.id, tooltips: ["Copier l'empreinte", "Copiée"] }}>
          {`${record.id.slice(0, 8)}…${record.id.slice(-8)}`}
        </Text>
      ),
    },
    {
      title: "Valide jusqu'au",
      key: "not_after",
      render: (_value, record) => formatDate(record.not_after),
    },
    {
      title: "Dernière utilisation",
      key: "last_used_at",
      render: (_value, record) => formatDateTime(record.last_used_at),
    },
    {
      title: "Statut",
      key: "status",
      render: (_value, record) => {
        const status = certificateStatus(record);
        return <Tag color={status.color}>{status.label}</Tag>;
      },
    },
  ];

  if (!readOnly) {
    columns.push({
      title: "Action",
      key: "action",
      render: (_value, record) =>
        record.revoked_at ? null : (
          <Popconfirm
            title="Révoquer ce certificat ? Les LRE présentées avec lui seront refusées immédiatement."
            okText="Révoquer"
            okType="danger"
            cancelText="Annuler"
            onConfirm={() =>
              runCallable(
                "revokeServiceAccountCertificate",
                { serviceAccountId: accountId, fingerprint: record.id },
                "Certificat révoqué"
              )
            }
          >
            <Button size="small" danger>
              Révoquer
            </Button>
          </Popconfirm>
        ),
    });
  }

  return (
    <div style={{ marginTop: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 4,
        }}
      >
        <Text strong>Certificats clients (lettre recommandée électronique)</Text>
        {!readOnly && (
          <Button
            size="small"
            icon={<PlusOutlined />}
            disabled={!account.is_active}
            onClick={() => setModalVisible(true)}
          >
            Ajouter un certificat
          </Button>
        )}
      </div>
      <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
        La LRE n'est pas encore ouverte via l'API. Elle exigera, en plus de la clé, ce certificat,
        que le serveur qui appelle l'API présentera à chaque connexion.
      </Text>
      <Table
        size="small"
        pagination={false}
        scroll={{ x: true }}
        loading={loading}
        dataSource={certificates}
        columns={columns}
        locale={{ emptyText: "Aucun certificat" }}
      />
      {!readOnly && (
        <CertificateModal
          visible={modalVisible}
          account={account}
          loading={saving}
          onSubmit={handleAdd}
          onCancel={() => setModalVisible(false)}
        />
      )}
    </div>
  );
}
